import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchMyAwards,
  claimCryptoAward,
  claimMerchAward,
  type AwardView,
} from "../api/awards.api";

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  DELIVERED: "Delivered",
  FAILED: "Failed",
};

const PRIZE_TYPE_EMOJI: Record<string, string> = {
  CRYPTO: "💰",
  MERCH: "👕",
  PERK: "⭐",
  FIAT_VOUCHER: "💵",
  TOKEN_REFUND: "🔄",
};

export function PrizesPanel() {
  const [awards, setAwards] = useState<AwardView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeClaim, setActiveClaim] = useState<AwardView | null>(null);

  const refresh = useCallback(async () => {
    try {
      setAwards(await fetchMyAwards());
      setError(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "Failed to load prizes");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleClose = () => {
    setActiveClaim(null);
    refresh();
  };

  return (
    <section className="prizes-panel">
      <h2>My Prizes</h2>

      {error && <div className="error-banner">{error}</div>}

      {awards.length === 0 && !error && (
        <div className="empty">
          No prizes yet. Spin the Lucky Wheel or grind the catalog!
        </div>
      )}

      <ul className="awards-list">
        {awards.map((a) => {
          const needsAction =
            (a.prizeType === "CRYPTO" && a.crypto?.status === "QUEUED" && !a.crypto?.recipientAddress) ||
            (a.prizeType === "MERCH" && a.merch?.status === "AWAITING_ADDRESS");
          return (
            <li
              key={a.id}
              className={`award-row ${needsAction ? "needs-action" : ""} ${
                a.status === "DELIVERED" ? "delivered" : ""
              }`}
            >
              <div className="award-icon">{PRIZE_TYPE_EMOJI[a.prizeType] ?? "🎁"}</div>
              <div className="award-info">
                <div className="award-name">{a.prizeName}</div>
                <div className="award-status">
                  <span className={`status-tag status-${a.status.toLowerCase()}`}>
                    {STATUS_LABELS[a.status] ?? a.status}
                  </span>
                  {a.crypto?.txHash && (
                    <span className="muted small">
                      · tx <code>{a.crypto.txHash.slice(0, 12)}…</code>
                    </span>
                  )}
                  {a.merch?.trackingNumber && (
                    <span className="muted small">· {a.merch.carrier} {a.merch.trackingNumber}</span>
                  )}
                </div>
              </div>
              {needsAction && (
                <button onClick={() => setActiveClaim(a)} type="button">
                  Claim
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <AnimatePresence>
        {activeClaim && (
          <ClaimModal
            award={activeClaim}
            onClose={handleClose}
            onError={setError}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function ClaimModal({
  award,
  onClose,
  onError,
}: {
  award: AwardView;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [tonAddress, setTonAddress] = useState("");
  const [merchForm, setMerchForm] = useState({
    recipientName: "",
    shippingAddress: "",
    city: "",
    country: "",
    postalCode: "",
    contactPhone: "",
  });

  const submit = async () => {
    setBusy(true);
    try {
      if (award.prizeType === "CRYPTO") {
        await claimCryptoAward(award.id, tonAddress);
      } else if (award.prizeType === "MERCH") {
        await claimMerchAward(award.id, merchForm);
      }
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      onError(msg ?? "Claim failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Claim {award.prizeName}</h3>

        {award.prizeType === "CRYPTO" && (
          <>
            <p className="muted small">
              Enter your TON wallet address. Payout: <strong>{award.crypto?.amount} {award.crypto?.currency}</strong>
            </p>
            <input
              type="text"
              placeholder="EQ... or UQ... TON address"
              value={tonAddress}
              onChange={(e) => setTonAddress(e.target.value)}
              autoFocus
            />
          </>
        )}

        {award.prizeType === "MERCH" && (
          <>
            <p className="muted small">Where should we ship your prize?</p>
            <input
              placeholder="Full name"
              value={merchForm.recipientName}
              onChange={(e) => setMerchForm({ ...merchForm, recipientName: e.target.value })}
            />
            <input
              placeholder="Street address"
              value={merchForm.shippingAddress}
              onChange={(e) => setMerchForm({ ...merchForm, shippingAddress: e.target.value })}
            />
            <div className="form-row">
              <input
                placeholder="City"
                value={merchForm.city}
                onChange={(e) => setMerchForm({ ...merchForm, city: e.target.value })}
              />
              <input
                placeholder="Country"
                value={merchForm.country}
                onChange={(e) => setMerchForm({ ...merchForm, country: e.target.value })}
              />
            </div>
            <div className="form-row">
              <input
                placeholder="Postal code"
                value={merchForm.postalCode}
                onChange={(e) => setMerchForm({ ...merchForm, postalCode: e.target.value })}
              />
              <input
                placeholder="Phone (optional)"
                value={merchForm.contactPhone}
                onChange={(e) => setMerchForm({ ...merchForm, contactPhone: e.target.value })}
              />
            </div>
          </>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="ghost" type="button">Cancel</button>
          <button onClick={submit} disabled={busy} type="button">
            {busy ? "Submitting…" : "Submit"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
